import type { HarbourEngine } from "./engine";
import { boatStyleById } from "./boatFleet";
import { CHART as C, MONO_FONT as MONO, drawGameAsset, getGameAsset, roundRect, type GameAsset } from "./imageAssets";

type View = { w: number; h: number; dpr: number };

export function render(ctx: CanvasRenderingContext2D, g: HarbourEngine, view: View) {
  const { w, h, dpr } = view;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = C.paper;
  ctx.fillRect(0, 0, w, h);

  ctx.save();
  ctx.translate(w / 2 - g.camera.x, h / 2 - g.camera.y);

  const left = g.camera.x - w / 2;
  const top = g.camera.y - h / 2;
  const right = g.camera.x + w / 2;
  const bottom = g.camera.y + h / 2;

  drawWaterEnvironment(ctx, left, top, right, bottom);
  drawWorldFrame(ctx, g);
  drawRoute(ctx, g);

  drawWhale(ctx, g);
  for (const isle of g.world.islands) drawIsland(ctx, isle);
  for (const lm of g.world.landmarks) drawLandmark(ctx, lm);
  drawLighthouse(ctx, g);
  drawHarbour(ctx, g);
  drawDock(ctx, g);
  for (const p of g.world.ports) drawPort(ctx, p, g.visited.has(p.id), g.claimed === p.id);
  for (const rule of g.world.rules) drawRuleBuoy(ctx, rule, g.collectedRules.has(rule.id), g.time);
  for (const b of g.world.bottles) if (!g.readBottles.has(b.id)) drawBottle(ctx, b, g.time);
  for (const r of g.world.rafts) if (!g.rescuedRafts.has(r.id)) drawRaft(ctx, r, g.time);
  for (const f of g.world.fleet) drawFleetBoat(ctx, f, g.time);

  drawWake(ctx, g);
  drawCrewFollowers(ctx, g);
  drawBoat(ctx, g);
  if (g.nearby) drawPrompt(ctx, g);

  ctx.restore();

  // screen-space overlays
  drawCompass(ctx, g, view);
  drawMinimap(ctx, g, view);
}

function drawWaterEnvironment(ctx: CanvasRenderingContext2D, l: number, t: number, r: number, b: number) {
  const water = getGameAsset("water");
  if (!water) return;
  const tile = 720;
  const startX = Math.floor(l / tile) * tile;
  const startY = Math.floor(t / tile) * tile;
  for (let x = startX; x < r; x += tile) {
    for (let y = startY; y < b; y += tile) ctx.drawImage(water, x, y, tile, tile);
  }
}

function drawWorldFrame(ctx: CanvasRenderingContext2D, g: HarbourEngine) {
  ctx.strokeStyle = C.line;
  ctx.lineWidth = 2;
  ctx.strokeRect(6, 6, g.world.w - 12, g.world.h - 12);
  ctx.strokeStyle = "rgba(53,98,116,0.25)";
  ctx.lineWidth = 1;
  ctx.strokeRect(20, 20, g.world.w - 40, g.world.h - 40);
}

function drawRoute(ctx: CanvasRenderingContext2D, g: HarbourEngine) {
  ctx.strokeStyle = "rgba(34,48,60,0.18)";
  ctx.lineWidth = 2;
  ctx.setLineDash([7, 9]);
  ctx.beginPath();
  ctx.moveTo(g.world.dock.x, g.world.dock.y);
  ctx.quadraticCurveTo(g.world.dock.x + 120, 1100, g.world.harbour.x, g.world.harbour.y + 90);
  ctx.stroke();
  ctx.setLineDash([]);
}

function drawIsland(ctx: CanvasRenderingContext2D, isle: { x: number; y: number; r: number }) {
  drawGameAsset(ctx, "island", isle.x, isle.y, isle.r * 2.15, isle.r * 2.15);
}

function drawPort(ctx: CanvasRenderingContext2D, p: { x: number; y: number; label: string }, visited: boolean, claimed: boolean) {
  drawGameAsset(ctx, "portMarker", p.x, p.y - 8, 94, 94, { alpha: claimed ? 1 : 0.92 });
  ctx.save();
  ctx.font = `600 13px ${MONO}`;
  ctx.textAlign = "center";
  const text = p.label.toUpperCase();
  const width = ctx.measureText(text).width + 18;
  ctx.fillStyle = C.foam;
  roundRect(ctx, p.x - width / 2, p.y + 44, width, 22, 5);
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = C.ink;
  ctx.stroke();
  ctx.fillStyle = C.ink;
  ctx.fillText(text, p.x, p.y + 59);
  if (visited && !claimed) {
    ctx.fillStyle = C.signal;
    ctx.font = `600 10px ${MONO}`;
    ctx.fillText("VISITED", p.x, p.y + 79);
  }
  ctx.restore();
}

function drawBottle(ctx: CanvasRenderingContext2D, b: { x: number; y: number }, time: number) {
  const bob = Math.sin(time * 2 + b.x) * 2;
  drawGameAsset(ctx, "messageBottle", b.x, b.y + bob, 56, 48, { rotation: -0.45 + Math.sin(time + b.y) * 0.05 });
}

function drawRaft(ctx: CanvasRenderingContext2D, r: { x: number; y: number }, time: number) {
  drawGameAsset(ctx, "rescueRaft", r.x, r.y + Math.sin(time * 1.6 + r.x) * 2.5, 90, 88);
}

function drawLighthouse(ctx: CanvasRenderingContext2D, g: HarbourEngine) {
  const lh = g.world.lighthouse;
  const bob = g.reducedMotion ? 0 : Math.sin(g.time * 0.6) * 1.5;
  drawGameAsset(ctx, "lighthouse", lh.x, lh.y + bob, 255, 248, { anchorX: 0.33, anchorY: 0.5 });
  drawAssetLabel(ctx, lh.x, lh.y + 132, "THE KEEPER'S LOG");
}

function drawHarbour(ctx: CanvasRenderingContext2D, g: HarbourEngine) {
  const h = g.world.harbour;
  drawGameAsset(ctx, "harbour", h.x, h.y, 430, 272);
  drawAssetLabel(ctx, h.x, h.y - 144, "SAME BOAT HARBOUR");
}

function drawDock(ctx: CanvasRenderingContext2D, g: HarbourEngine) {
  const d = g.world.dock;
  drawGameAsset(ctx, "dock", d.x, d.y + 20, 154, 103);
  drawAssetLabel(ctx, d.x, d.y + 82, "THE BOATYARD");
}

function drawAssetLabel(ctx: CanvasRenderingContext2D, x: number, y: number, text: string) {
  ctx.save();
  ctx.fillStyle = C.ink;
  ctx.font = `600 11px ${MONO}`;
  ctx.textAlign = "center";
  ctx.fillText(text, x, y);
  ctx.restore();
}

function drawWake(ctx: CanvasRenderingContext2D, g: HarbourEngine) {
  const t = g.trail;
  if (t.length < 2) return;
  for (let i = 1; i < t.length; i++) {
    const a = (i / t.length) * 0.5;
    ctx.strokeStyle = `rgba(53,98,116,${a * 0.6})`;
    ctx.lineWidth = (i / t.length) * 6 + 0.5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(t[i - 1].x, t[i - 1].y);
    ctx.lineTo(t[i].x, t[i].y);
    ctx.stroke();
  }
}

function drawCrewFollowers(ctx: CanvasRenderingContext2D, g: HarbourEngine) {
  const spacing = 64;
  g.crew.forEach((_, i) => {
    const p = pointBehindBoat(g, spacing * (i + 1));
    drawOtter(ctx, p.x, p.y, 0.82);
  });
}

function pointBehindBoat(g: HarbourEngine, targetDistance: number) {
  let cursor = { x: g.boat.x, y: g.boat.y };
  let travelled = 0;

  for (let i = g.trail.length - 1; i >= 0; i -= 1) {
    const point = g.trail[i];
    const segment = Math.hypot(cursor.x - point.x, cursor.y - point.y);
    if (segment > 0 && travelled + segment >= targetDistance) {
      const amount = (targetDistance - travelled) / segment;
      return {
        x: cursor.x + (point.x - cursor.x) * amount,
        y: cursor.y + (point.y - cursor.y) * amount,
      };
    }
    travelled += segment;
    cursor = point;
  }

  // A newly rescued crew member may not have enough wake yet. Keep them in a
  // clear formation behind the stern until the travelled path is long enough.
  return {
    x: g.boat.x - Math.cos(g.boat.heading) * targetDistance,
    y: g.boat.y - Math.sin(g.boat.heading) * targetDistance,
  };
}

function drawOtter(ctx: CanvasRenderingContext2D, x: number, y: number, s: number) {
  drawGameAsset(ctx, "crewOtter", x, y, 45 * s, 52 * s);
}

function drawBoat(ctx: CanvasRenderingContext2D, g: HarbourEngine) {
  const b = g.boat;
  const bob = g.reducedMotion ? 0 : Math.sin(g.time * 2.4) * 1.5;
  const asset = boatStyleById(g.boatStyle)?.asset ?? "playerBoat";
  drawGameAsset(ctx, asset, b.x, b.y + bob, 38, 82, { rotation: b.heading + Math.PI / 2 });
}

function drawFleetBoat(ctx: CanvasRenderingContext2D, boat: { x: number; y: number; heading: number }, time: number) {
  // Keep the phase tied to the lane, not the changing x-position. Using x here
  // turns forward movement into a high-frequency vertical vibration.
  const lanePhase = boat.y * 0.017;
  const drift = Math.sin(time * 0.72 + lanePhase) * 0.8;
  drawGameAsset(ctx, "fleetBoat", boat.x, boat.y + drift, 76, 34, { rotation: boat.heading });
}

function drawLandmark(ctx: CanvasRenderingContext2D, landmark: { kind: "table" | "workshop" | "noticewall"; x: number; y: number; name: string }) {
  const key: GameAsset =
    landmark.kind === "table" ? "landmarkTable" : landmark.kind === "workshop" ? "landmarkWorkshop" : "landmarkNoticewall";
  drawGameAsset(ctx, key, landmark.x, landmark.y, 168, 160);
  drawAssetLabel(ctx, landmark.x, landmark.y + 94, landmark.name.toUpperCase());
}

function drawRuleBuoy(ctx: CanvasRenderingContext2D, rule: { x: number; y: number; index: number }, collected: boolean, time: number) {
  const bob = Math.sin(time * 2.2 + rule.x) * 2;
  drawGameAsset(ctx, "ruleBuoy", rule.x, rule.y + bob, 58, 60, { alpha: collected ? 0.5 : 1 });
  ctx.save();
  ctx.fillStyle = collected ? C.signalDeep : C.ink;
  ctx.font = `700 10px ${MONO}`;
  ctx.textAlign = "center";
  ctx.fillText(collected ? "OK" : String(rule.index + 1), rule.x, rule.y + 38 + bob);
  ctx.restore();
}

function drawWhale(ctx: CanvasRenderingContext2D, g: HarbourEngine) {
  const whale = g.world.whale;
  if (!whale.surfaced) return;
  drawGameAsset(ctx, "whale", whale.x, whale.y + Math.sin(g.time * 1.2) * 2, 166, 114);
}

function drawPrompt(ctx: CanvasRenderingContext2D, g: HarbourEngine) {
  const n = g.nearby!;
  ctx.save();
  ctx.translate(n.x, n.y - 64);
  ctx.font = `600 13px ${MONO}`;
  ctx.textAlign = "center";
  const text = n.prompt.toUpperCase();
  const wpx = ctx.measureText(text).width + 30;
  ctx.fillStyle = C.ink;
  roundRect(ctx, -wpx / 2, -16, wpx, 26, 13);
  ctx.fill();
  ctx.fillStyle = C.foam;
  ctx.fillText(text, 0, 2);
  // little pointer
  ctx.beginPath();
  ctx.moveTo(-5, 10);
  ctx.lineTo(5, 10);
  ctx.lineTo(0, 17);
  ctx.closePath();
  ctx.fillStyle = C.ink;
  ctx.fill();
  ctx.restore();
}

function drawCompass(ctx: CanvasRenderingContext2D, g: HarbourEngine, view: View) {
  const cx = view.w - 58;
  const cy = view.h - 58;
  ctx.save();
  const ang = Math.atan2(g.world.harbour.y - g.boat.y, g.world.harbour.x - g.boat.x);
  ctx.translate(cx, cy);
  ctx.fillStyle = "rgba(250,247,239,0.92)";
  ctx.strokeStyle = C.ink;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(0, 0, 31, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.rotate(ang);
  ctx.fillStyle = C.signal;
  ctx.beginPath();
  ctx.moveTo(21, 0);
  ctx.lineTo(-8, -8);
  ctx.lineTo(-3, 0);
  ctx.lineTo(-8, 8);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
  ctx.fillStyle = C.muted;
  ctx.font = `600 9px ${MONO}`;
  ctx.textAlign = "center";
  ctx.fillText("HARBOUR", cx, cy + 44);
}

function drawMinimap(ctx: CanvasRenderingContext2D, g: HarbourEngine, view: View) {
  // The top-right page/app controls occupy the first row. Keep the chart below
  // them so neither the buttons nor the map have to compete for contrast.
  const compact = view.w < 640;
  const mw = compact ? 104 : 148;
  const mh = mw * (g.world.h / g.world.w);
  const mx = view.w - mw - (compact ? 12 : 20);
  const my = compact ? 68 : 76;
  const sx = mw / g.world.w;
  const sy = mh / g.world.h;
  ctx.save();
  ctx.fillStyle = "rgba(250,247,239,0.92)";
  ctx.strokeStyle = C.ink;
  ctx.lineWidth = 1.5;
  roundRect(ctx, mx, my, mw, mh, 6);
  ctx.fill();
  ctx.stroke();
  const px = (x: number) => mx + x * sx;
  const py = (y: number) => my + y * sy;
  // islands
  ctx.fillStyle = "rgba(201,185,143,0.7)";
  for (const isle of g.world.islands) {
    ctx.beginPath();
    ctx.arc(px(isle.x), py(isle.y), isle.r * sx, 0, Math.PI * 2);
    ctx.fill();
  }
  // ports
  for (const p of g.world.ports) {
    ctx.beginPath();
    ctx.arc(px(p.x), py(p.y), 3, 0, Math.PI * 2);
    ctx.fillStyle = g.claimed === p.id ? C.signal : g.visited.has(p.id) ? C.signalDeep : C.sea;
    ctx.fill();
  }
  // landmarks
  ctx.fillStyle = C.sandLine;
  for (const lm of g.world.landmarks) {
    ctx.fillRect(px(lm.x) - 2.5, py(lm.y) - 2.5, 5, 5);
  }
  // rule buoys
  for (const rule of g.world.rules) {
    ctx.beginPath();
    ctx.arc(px(rule.x), py(rule.y), 2.4, 0, Math.PI * 2);
    ctx.fillStyle = g.collectedRules.has(rule.id) ? C.signal : C.gold;
    ctx.fill();
  }
  // fleet (moving)
  ctx.fillStyle = C.sea;
  for (const f of g.world.fleet) {
    ctx.beginPath();
    ctx.arc(px(f.x), py(f.y), 2.4, 0, Math.PI * 2);
    ctx.fill();
  }
  // harbour
  ctx.beginPath();
  ctx.arc(px(g.world.harbour.x), py(g.world.harbour.y), 4, 0, Math.PI * 2);
  ctx.fillStyle = C.gold;
  ctx.fill();
  ctx.strokeStyle = C.ink;
  ctx.lineWidth = 1;
  ctx.stroke();
  // boat
  ctx.beginPath();
  ctx.arc(px(g.boat.x), py(g.boat.y), 3.5, 0, Math.PI * 2);
  ctx.fillStyle = C.ink;
  ctx.fill();
  ctx.restore();
}
